import {
  Box,
  Container,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@chakra-ui/react';
import { usePageViewTracker } from 'context/PageViewTracker';
import {
  defaultLanguageSelector,
  getSessionLanguage,
  languagesSelectorList,
  setSessionLanguage,
  taxonomy
} from 'locales/helpers';
import { Locales } from 'locales/i18nLingui';
import React from 'react';
import { useHistory, useParams } from 'react-router';
import useSingleTrackDetails from 'services/libraryItem/hooks/useSingleTrackDetails';
import { getEnglishSlug, getTranslatedSlug } from 'views_2/Topics/intl/helpers';
import LanguageButton from './LanguageButton';
import SelectorButton, { LanguageSelectorVariant } from './SelectorButton';

interface PageParams {
  lang?: string;
  slug?: string;
  teacher?: string;
}

interface LanguageSelectorProps {
  variant?: LanguageSelectorVariant;
}

const LanguageSelector = ({ variant = 'light' }: LanguageSelectorProps) => {
  const isDark = variant === 'dark';
  const history = useHistory();
  const activeLanguage = getSessionLanguage();
  // FooterFlux spells out the default language; the menu still lists "USA".
  const activeLanguageLabel =
    isDark && activeLanguage?.locale === defaultLanguageSelector.locale
      ? 'English (United States)'
      : activeLanguage?.name;
  const { pageType } = usePageViewTracker();
  const { lang, slug } = useParams<PageParams>();
  const { libraryItemDetails: libraryItem } = useSingleTrackDetails({
    singleTrackIdOrSlug:
      pageType === 'gm_details_page' && slug ? slug : undefined
  });

  const handleChangeLanguage = (locale: Locales) => {
    const taxonomyLang = taxonomy(locale);
    setSessionLanguage(locale);

    // reset password page
    if (pageType === 'reset_password_page') {
      history.push(taxonomyLang.getResetPasswordUrl());
    }

    // teacher page
    else if (pageType === 'teacher_profile_page') {
      history.push(taxonomyLang.getTeacherUrl(slug));
    }

    // course detail page
    else if (pageType === 'course_details_page') {
      if (slug) {
        history.push(taxonomyLang.getCourseUrl(slug));
      }
    }

    // gm detail page
    else if (pageType === 'gm_details_page') {
      if (libraryItem?.slug && libraryItem?.publisher?.username)
        history.push(
          taxonomyLang.getSinglesUrl(
            libraryItem.slug,
            libraryItem.publisher.username
          )
        );
    }

    // topic detail page
    else if (pageType === 'topic_details_page') {
      if (slug) {
        const englishSlug = getEnglishSlug(slug, lang);

        const translatedSlug = getTranslatedSlug(
          englishSlug,
          taxonomyLang.config.home
        );

        if (translatedSlug) {
          history.push(taxonomyLang.getTopicUrl(translatedSlug));
        }
      }
    }

    // other page
    else {
      history.push(taxonomyLang.getHomeUrl());
    }
  };

  return (
    <Box pr={isDark ? 0 : '10px'}>
      <Popover placement={isDark ? 'top-end' : 'right'}>
        <PopoverTrigger>
          <SelectorButton
            selectedLanguage={activeLanguageLabel}
            variant={variant}
          />
        </PopoverTrigger>
        <PopoverContent
          _focus={{
            outline: 'none'
          }}
          width="230px"
          boxShadow="lg"
        >
          <Container width="200px" id="language-selector">
            {languagesSelectorList.map(language => (
              <LanguageButton
                key={language.name}
                onClick={() => {
                  handleChangeLanguage(language.locale);
                }}
                selected={language.name === activeLanguage?.name}
              >
                {language.name}
              </LanguageButton>
            ))}
          </Container>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default LanguageSelector;
